<template>
  <div class="notification-page">
    <div class="page-heading">
      <div>
        <p>运营提醒</p>
        <h1>通知中心</h1>
        <span>集中查看到期提醒、节点异常和服务器变更事件</span>
      </div>
      <div class="heading-actions">
        <a-button :disabled="summary.unread_count === 0" @click="onReadAll">全部已读</a-button>
        <a-button type="primary" @click="router.push('/settings?tab=notifications')">提醒设置</a-button>
      </div>
    </div>

    <div class="summary-grid">
      <div><span>未读</span><strong>{{ summary.unread_count }}</strong></div>
      <div><span>待处理</span><strong>{{ summary.open_count }}</strong></div>
      <div class="urgent"><span>紧急</span><strong>{{ summary.by_stage.urgent || 0 }}</strong></div>
      <div class="expired"><span>已过期</span><strong>{{ summary.by_stage.expired || 0 }}</strong></div>
    </div>

    <a-card :bordered="false" class="filter-card">
      <div class="filters">
        <a-select v-model:value="filters.type" allow-clear placeholder="全部类型" style="width: 140px" @change="reloadFromStart">
          <a-select-option value="node_expiry">节点到期</a-select-option>
          <a-select-option value="domain_ip_change">域名 IP 变更</a-select-option>
          <a-select-option value="node_unavailable">节点不可用</a-select-option>
          <a-select-option value="proxy_unavailable">节点不可用</a-select-option>
          <a-select-option value="proxy_expiry">节点到期</a-select-option>
        </a-select>
        <a-select v-model:value="filters.status" allow-clear placeholder="全部状态" style="width: 130px" @change="reloadFromStart">
          <a-select-option value="open">待处理</a-select-option>
          <a-select-option value="resolved">已解决</a-select-option>
          <a-select-option value="ignored">已忽略</a-select-option>
        </a-select>
        <a-select v-model:value="filters.stage" allow-clear placeholder="全部级别" style="width: 130px" @change="reloadFromStart">
          <a-select-option value="upcoming">即将到期</a-select-option>
          <a-select-option value="soon">临近</a-select-option>
          <a-select-option value="urgent">紧急</a-select-option>
          <a-select-option value="expired">已过期</a-select-option>
        </a-select>
        <a-select v-model:value="filters.read" allow-clear placeholder="已读状态" style="width: 120px" @change="reloadFromStart">
          <a-select-option value="unread">未读</a-select-option>
          <a-select-option value="read">已读</a-select-option>
        </a-select>
        <a-input-search v-model:value="filters.keyword" allow-clear placeholder="节点、IP、用户或端口" style="min-width: 210px; flex: 1" @search="reloadFromStart" />
      </div>
    </a-card>

    <a-card :bordered="false" class="list-card">
      <a-table
        v-if="!isMobile"
        class="desktop-table"
        data-testid="notification-desktop-list"
        :columns="columns"
        :data-source="items"
        :loading="loading"
        :pagination="false"
        row-key="id"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'resource'">
            <div class="resource-cell" :class="{ unread: !record.read_at }">
              <a-tag :color="record.type === 'proxy_expiry' ? 'purple' : record.type === 'domain_ip_change' ? 'red' : 'blue'">{{ typeLabel(record.type) }}</a-tag>
              <div><strong>{{ record.node_name || '未知节点' }}</strong><small>{{ resourceDetail(record) }}</small></div>
            </div>
          </template>
          <template v-else-if="column.key === 'stage'">
            <a-tag :color="stageColor(record.stage)">{{ notificationLevelLabel(record) }}</a-tag>
          </template>
          <template v-else-if="column.key === 'expire_at'">
            <div><small class="time-kind">{{ notificationTimeLabel(record) }}</small><strong>{{ formatTime(record.expire_at) }}</strong><small v-if="isExpiryNotification(record)" class="remaining">{{ remainingLabel(record.days_remaining) }}</small></div>
          </template>
          <template v-else-if="column.key === 'status'">{{ statusLabel(record.status) }}</template>
          <template v-else-if="column.key === 'actions'">
            <a-space>
              <a-button type="link" size="small" @click="openResource(record)">查看</a-button>
              <a-button v-if="!record.read_at" type="link" size="small" @click="onRead(record)">已读</a-button>
              <a-popconfirm v-if="record.status === 'open'" title="确认忽略这条提醒？" @confirm="onIgnore(record)">
                <a-button type="link" danger size="small">忽略</a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>

      <a-spin v-else :spinning="loading" class="mobile-list" data-testid="notification-mobile-list">
        <a-empty v-if="!loading && items.length === 0" description="暂无通知" />
        <article v-for="item in items" :key="item.id" class="notification-card" :class="{ unread: !item.read_at }">
          <div class="card-top">
          <div><a-tag :color="item.type === 'proxy_expiry' ? 'purple' : item.type === 'domain_ip_change' ? 'red' : 'blue'">{{ typeLabel(item.type) }}</a-tag><a-tag :color="stageColor(item.stage)">{{ notificationLevelLabel(item) }}</a-tag></div>
            <span>{{ statusLabel(item.status) }}</span>
          </div>
          <h3>{{ item.title }}</h3>
          <p>{{ item.message }}</p>
          <div class="card-expire"><span>{{ notificationTimeLabel(item) }}</span><strong>{{ formatTime(item.expire_at) }}</strong><small v-if="isExpiryNotification(item)">{{ remainingLabel(item.days_remaining) }}</small></div>
          <div class="card-actions">
            <a-button size="small" @click="openResource(item)">查看资源</a-button>
            <a-button v-if="!item.read_at" size="small" @click="onRead(item)">标为已读</a-button>
            <a-popconfirm v-if="item.status === 'open'" title="确认忽略这条提醒？" @confirm="onIgnore(item)">
              <a-button danger size="small">忽略</a-button>
            </a-popconfirm>
          </div>
        </article>
      </a-spin>

      <div v-if="total > pageSize" class="pagination-wrap">
        <a-pagination v-model:current="page" :page-size="pageSize" :total="total" :show-size-changer="false" @change="load" />
      </div>
    </a-card>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { proxyAvailabilityErrorLabel } from '../components/notificationErrorLabel';
import {
  getNotificationSummary,
  ignoreNotification,
  listNotifications,
  readAllNotifications,
  readNotification,
  type NotificationItem,
  type NotificationStage,
  type NotificationSummary,
  type NotificationType,
} from '../api/notifications';

const router = useRouter();
const items = ref<NotificationItem[]>([]);
const loading = ref(false);
const page = ref(1);
const pageSize = 30;
const total = ref(0);
const isMobile = ref(false);
const summary = reactive<NotificationSummary>({ unread_count: 0, open_count: 0, by_stage: { upcoming: 0, soon: 0, urgent: 0, expired: 0 } });
const filters = reactive({ type: undefined as NotificationType | undefined, status: undefined as string | undefined, stage: undefined as NotificationStage | undefined, read: undefined as string | undefined, keyword: '' });
const columns = [
  { title: '提醒对象', key: 'resource', width: 290 },
  { title: '级别', key: 'stage', width: 100 },
  { title: '时间', key: 'expire_at', width: 190 },
  { title: '状态', key: 'status', width: 90 },
  { title: '操作', key: 'actions', width: 180 },
];

const loadSummary = async () => Object.assign(summary, await getNotificationSummary());
const load = async () => {
  loading.value = true;
  try {
    const data = await listNotifications({ ...filters, limit: pageSize, offset: (page.value - 1) * pageSize });
    items.value = data.items || [];
    total.value = data.total || 0;
    await loadSummary();
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '加载通知失败');
  } finally {
    loading.value = false;
  }
};
const reloadFromStart = () => { page.value = 1; load(); };
const notifySummaryChanged = () => window.dispatchEvent(new Event('notification-summary-changed'));
const onRead = async (item: NotificationItem) => { await readNotification(item.id); item.read_at = new Date().toISOString(); await loadSummary(); notifySummaryChanged(); };
const onReadAll = async () => { await readAllNotifications(); message.success('已全部标为已读'); await load(); notifySummaryChanged(); };
const onIgnore = async (item: NotificationItem) => { await ignoreNotification(item.id); message.success('已忽略'); await load(); notifySummaryChanged(); };
const openResource = async (item: NotificationItem) => {
  if (!item.read_at) await onRead(item);
  router.push(item.type === 'proxy_expiry' || item.type === 'proxy_unavailable' ? '/proxies' : '/nodes');
};
const typeLabel = (type: NotificationType) => type === 'proxy_unavailable' ? '节点不可用' : type === 'node_unavailable' ? '服务器不可用' : type === 'domain_ip_change' ? '域名 IP 变更' : type === 'node_expiry' ? '服务器' : '节点';
const stageLabel = (stage: NotificationStage) => ({ upcoming: '即将到期', soon: '临近', urgent: '紧急', expired: '已过期' }[stage]);
const isExpiryNotification = (item: NotificationItem) => item.type === 'proxy_expiry' || item.type === 'node_expiry';
const notificationTimeLabel = (item: NotificationItem) => isExpiryNotification(item) ? '到期时间' : '检测时间';
const notificationLevelLabel = (item: NotificationItem) => item.type === 'proxy_unavailable' ? proxyAvailabilityErrorLabel(item.message) : item.type === 'domain_ip_change' ? '变更' : isExpiryNotification(item) ? stageLabel(item.stage) : '异常';
const stageColor = (stage: NotificationStage) => ({ upcoming: 'blue', soon: 'orange', urgent: 'red', expired: 'default' }[stage]);
const statusLabel = (status: string) => ({ open: '待处理', resolved: '已解决', ignored: '已忽略' }[status] || status);
const resourceDetail = (item: NotificationItem) => item.type === 'proxy_expiry' || item.type === 'proxy_unavailable' ? `${item.proxy_user_nickname || '未归属'} · 端口 ${item.port || '-'}` : (item.node_ip || '未记录出口 IP');
const formatTime = (value: string) => new Date(value).toLocaleString('zh-CN', { hour12: false });
const remainingLabel = (days: number) => days < 0 ? `已过期 ${Math.abs(days)} 天` : days === 0 ? '今天到期' : `剩余 ${days} 天`;

const syncViewport = () => { isMobile.value = window.innerWidth <= 767; };

onMounted(() => {
  syncViewport();
  window.addEventListener('resize', syncViewport);
  load();
});
onBeforeUnmount(() => window.removeEventListener('resize', syncViewport));
</script>

<style scoped>
.notification-page { max-width: 1240px; margin: 0 auto; }
.page-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; margin-bottom: 18px; }
.page-heading p { margin: 0 0 3px; color: #0b68cb; font-size: 11px; letter-spacing: .12em; }
.page-heading h1 { margin: 0; color: #15273a; font-size: 25px; }
.page-heading span { display: block; margin-top: 4px; color: #7a8797; font-size: 13px; }
.heading-actions { display: flex; gap: 8px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-bottom: 12px; }
.summary-grid > div { padding: 16px 18px; border: 1px solid #e3e9f0; border-radius: 12px; background: #fff; }
.summary-grid span, .summary-grid strong { display: block; }
.summary-grid span { color: #778598; font-size: 12px; }
.summary-grid strong { margin-top: 5px; color: #17304b; font-size: 25px; }
.summary-grid .urgent strong { color: #d4380d; }
.summary-grid .expired strong { color: #6b7280; }
.filter-card { margin-bottom: 12px; }
.filters { display: flex; flex-wrap: wrap; gap: 10px; }
.list-card :deep(.ant-card-body) { padding: 0; }
.resource-cell { display: flex; align-items: center; gap: 8px; }
.resource-cell.unread strong::after { content: ''; display: inline-block; width: 6px; height: 6px; margin-left: 7px; border-radius: 50%; background: #1677ff; vertical-align: middle; }
.resource-cell strong, .resource-cell small, .remaining { display: block; }
.resource-cell small, .remaining { margin-top: 3px; color: #8793a3; font-size: 11px; }
.time-kind { display: block; margin-bottom: 3px; color: #8793a3; font-size: 11px; }
.pagination-wrap { display: flex; justify-content: flex-end; padding: 16px; border-top: 1px solid #edf0f4; }
@media (max-width: 767px) {
  .page-heading { align-items: flex-start; }
  .page-heading h1 { font-size: 22px; }
  .page-heading > div:first-child span { display: none; }
  .heading-actions .ant-btn:first-child { display: none; }
  .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  .summary-grid > div { padding: 12px 14px; }
  .summary-grid strong { font-size: 21px; }
  .filters > * { width: calc(50% - 5px) !important; min-width: 0 !important; }
  .filters :last-child { width: 100% !important; }
  .mobile-list { display: block; min-height: 120px; padding: 10px; }
  .notification-card { padding: 14px; border: 1px solid #e3e9f0; border-radius: 12px; background: #fff; }
  .notification-card + .notification-card { margin-top: 9px; }
  .notification-card.unread { border-left: 3px solid #1677ff; }
  .card-top { display: flex; align-items: center; justify-content: space-between; color: #8793a3; font-size: 11px; }
  .notification-card h3 { margin: 11px 0 5px; color: #172b40; font-size: 16px; }
  .notification-card p { margin: 0; color: #667487; font-size: 12px; line-height: 1.6; }
  .card-expire { display: grid; grid-template-columns: 1fr auto; gap: 3px 8px; margin-top: 12px; padding: 10px; border-radius: 9px; background: #f5f7fa; }
  .card-expire span { color: #8793a3; font-size: 11px; }
  .card-expire strong { color: #2d3d50; font-size: 12px; }
  .card-expire small { grid-column: 1 / -1; color: #d4380d; }
  .card-actions { display: flex; gap: 7px; margin-top: 12px; }
  .pagination-wrap { justify-content: center; }
}
</style>
